import { useEffect, useState, type ReactNode } from 'react'

import { Badge } from '../../../../domains/ontology-ui/main/src/components/ui/badge'
import { Button } from '../../../../domains/ontology-ui/main/src/components/ui/button'
import { Textarea } from '../../../../domains/ontology-ui/main/src/components/ui/textarea'

import {
  AGENT_FINDING_FEEDBACK_MAX_CHARS,
  AGENT_FINDINGS_REVISION_SCOPE,
  bumpDataRevision,
  batchAgentFindings,
  patchAgentFinding,
  patchAgentFindingsSettled,
  type AgentFinding,
  type AgentFindingApprovalStatus
} from '../data-runtime'
import {
  isReviewableFindingType,
  resolveReviewActionsTarget,
  type ReviewActionsProps
} from './review-actions-model'

export type { ReviewActionsProps, ReviewActionsTarget } from './review-actions-model'
export { isReviewableFindingType, resolveReviewActionsTarget } from './review-actions-model'

function statusLabel(status: AgentFindingApprovalStatus): string {
  if (status === 'approved') return 'Approved'
  if (status === 'rejected') return 'Rejected'
  return 'Awaiting review'
}

function applyFinding(
  finding: AgentFinding,
  setStatus: (next: AgentFindingApprovalStatus) => void
) {
  setStatus(finding.approvalStatus)
  bumpDataRevision(AGENT_FINDINGS_REVISION_SCOPE)
}

/**
 * Host-authored review control. Agents may place it; only its click
 * handlers PATCH. Not in `run_dashboard_script` scope.
 */
export function ReviewActions(props: ReviewActionsProps): ReactNode {
  const target = resolveReviewActionsTarget(props)
  const reviewable = isReviewableFindingType(props.resultType, props.reviewableTypes)

  if (!reviewable) return null
  if (target.kind === 'misuse') {
    return <ReviewActionsWarning message={target.message} />
  }
  if (target.kind === 'item') {
    return (
      <ItemReview
        resultId={target.id}
        itemId={props.itemId}
        runId={props.runId}
        status={props.status}
      />
    )
  }
  return <ResolveReview resultIds={target.ids} />
}

function ItemReview({
  resultId,
  itemId,
  runId,
  status
}: {
  resultId: string
  itemId?: string
  runId?: string
  status: AgentFindingApprovalStatus | undefined
}): ReactNode {
  const [current, setCurrent] = useState<AgentFindingApprovalStatus>(status ?? 'awaiting_review')
  const [composing, setComposing] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setCurrent(status ?? 'awaiting_review')
  }, [status])

  const run = async (approvalStatus: AgentFindingApprovalStatus, feedback?: string) => {
    if (approvalStatus === 'rejected' && !feedback?.trim()) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (approvalStatus === 'rejected') {
        const batch = await batchAgentFindings({
          itemIds: [itemId?.trim() || resultId],
          approvalStatus: 'rejected',
          feedback
        })
        const finding = batch.results[0]
        if (finding) applyFinding(finding, setCurrent)
        else setCurrent('rejected')
      } else {
        const finding = await patchAgentFinding({ id: resultId, approvalStatus, feedback })
        applyFinding(finding, setCurrent)
      }
      setComposing(false)
      setReason('')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{statusLabel(current)}</Badge>
        <Button type="button" size="sm" disabled={busy} onClick={() => void run('approved')}>
          Approve
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => {
            setComposing(true)
            setError(null)
          }}
        >
          Reject
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => void run('awaiting_review')}
        >
          Reset to awaiting
        </Button>
      </div>
      {composing ? (
        <DenialComposer
          value={reason}
          onChange={setReason}
          disabled={busy}
          onCancel={() => {
            setComposing(false)
            setReason('')
          }}
          onConfirm={() => void run('rejected', reason)}
        />
      ) : null}
      {current === 'rejected' && runId ? (
        <a
          href={`/runs?type=custom&run=${encodeURIComponent(runId)}&tab=agent`}
          className="text-xs font-medium text-brand hover:underline"
        >
          Explain on the run →
        </a>
      ) : null}
      {error ? <p className="m-0 text-xs text-st-down">{error}</p> : null}
    </div>
  )
}

function ResolveReview({ resultIds }: { resultIds: string[] }): ReactNode {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<string | null>(null)
  const [retryIds, setRetryIds] = useState<string[]>([])
  const [retryStatus, setRetryStatus] = useState<AgentFindingApprovalStatus>('approved')

  const run = async (approvalStatus: AgentFindingApprovalStatus, ids: string[]) => {
    setBusy(true)
    setError(null)
    try {
      const { ok, failed } = await patchAgentFindingsSettled(ids, approvalStatus)
      if (ok.length > 0) {
        bumpDataRevision(AGENT_FINDINGS_REVISION_SCOPE)
      }
      if (failed.length === 0) {
        setSummary(approvalStatus === 'approved' ? `Resolved ${ok.length}` : `Reset ${ok.length}`)
        setRetryIds([])
        return
      }
      const verb = approvalStatus === 'approved' ? 'Resolved' : 'Reset'
      setSummary(`${verb} ${ok.length} of ${ok.length + failed.length}`)
      setRetryIds(failed.map((row) => row.id))
      setRetryStatus(approvalStatus)
      setError(failed.map((row) => row.error).join(' · '))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          disabled={busy}
          onClick={() => void run('approved', resultIds)}
        >
          Resolve
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => void run('awaiting_review', resultIds)}
        >
          Undo
        </Button>
        {retryIds.length > 0 ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => void run(retryStatus, retryIds)}
          >
            Retry failed
          </Button>
        ) : null}
      </div>
      {summary ? <p className="m-0 text-xs text-ink-3">{summary}</p> : null}
      {error ? <p className="m-0 text-xs text-st-down">{error}</p> : null}
    </div>
  )
}

function DenialComposer({
  value,
  onChange,
  onCancel,
  onConfirm,
  disabled
}: {
  value: string
  onChange: (next: string) => void
  onCancel: () => void
  onConfirm: () => void
  disabled: boolean
}): ReactNode {
  const trimmed = value.trim()
  const canConfirm = trimmed.length > 0 && !disabled
  return (
    <div className="flex flex-col gap-2 rounded-md border border-st-down/40 bg-st-down-bg/20 p-3">
      <label className="text-[11px] font-medium uppercase tracking-wide text-st-down">
        Why is this wrong?
      </label>
      <Textarea
        autoFocus
        rows={3}
        maxLength={AGENT_FINDING_FEEDBACK_MAX_CHARS}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder="e.g. this is a planned downtime, not a fault…"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-ink-3">
          {value.length}/{AGENT_FINDING_FEEDBACK_MAX_CHARS}
        </span>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" disabled={!canConfirm} onClick={onConfirm}>
            Confirm reject
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={disabled} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

function ReviewActionsWarning({ message }: { message: string }): ReactNode {
  return (
    <div className="rounded-md border border-st-down/40 bg-st-down-bg px-3 py-2 text-xs text-st-down">
      {message}
    </div>
  )
}
